import { MutableRefObject, useRef, useState } from 'react';
import videojs, { VideoJsPlayerOptions } from 'video.js';

export type VideoJsPlayer = videojs.Player;

export const useVideoPlayer = () => {
  const player_ref: MutableRefObject<VideoJsPlayer | null> = useRef<VideoJsPlayer>(null);

  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(true);
  const [current_time, setCurrentTime] = useState(0);

  const init = (
    video_element: HTMLVideoElement,
    options: VideoJsPlayerOptions,
    {
      onReady,
      onView,
      onEnded,
    }: {
      onReady?: () => void;
      onView?: () => void;
      onEnded?: () => void;
    },
  ) => {
    try {
      if (!player_ref.current) {
        const player = (player_ref.current = videojs(video_element, options, onReady));
        player.on('pause', () => setPaused(true));
        player.on('play', () => {
          setPaused(false);
        });
        player.on('timeupdate', () => {
          setCurrentTime(player.currentTime());
          if (player.duration() * 0.97 < player.currentTime()) {
            onView?.();
          }
        });
        player.on('ended', () => {
          onEnded?.();
        });
      } else {
        options.sources && player_ref.current.src(options.sources);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const destroy = () => {
    player_ref.current?.off();
    player_ref.current?.dispose();
    player_ref.current = null;
  };

  const togglePlay = () => {
    if (player_ref.current?.paused()) {
      player_ref.current?.play();
    } else {
      player_ref.current?.pause();
    }
  };

  const toggleMute = () => {
    if (player_ref.current?.muted()) {
      player_ref.current.volume(1);
      player_ref.current.muted(false);
      setMuted(false);
    } else {
      player_ref.current?.volume(0);
      player_ref.current?.muted(true);
      setMuted(true);
    }
  };

  const getDuration = () => {
    const duration = player_ref.current?.duration() ?? 0;
    return Number.isNaN(duration) ? 0 : duration;
  };

  return {
    player: player_ref.current,
    paused,
    current_time,
    init,
    destroy,
    togglePlay,
    toggleMute,
    getDuration,
    muted,
  };
};
